import { MasterBarcodeEntry } from '../types';

export type BarcodeRowTextLayoutMode = 'inline' | 'split';

export interface BarcodeRowResponsiveLayout {
  barcodeMaxCharacters: number;
  textLayoutMode: BarcodeRowTextLayoutMode;
  abbreviateStores: boolean;
}

const LEFT_ELLIPSIS = '..';
const MIN_BARCODE_SUFFIX_DIGITS = 4;
const MIN_LEFT_ELLIPSIS_LENGTH = LEFT_ELLIPSIS.length + MIN_BARCODE_SUFFIX_DIGITS;
const INLINE_TEXT_THRESHOLD = 240;
const ABBREVIATED_STORES_THRESHOLD = 190;
const MONO_CHAR_WIDTH_PX = 8.6;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

export function shortenBarcodeFromLeft(barcode: string, maxCharacters: number): string {
  if (barcode.length <= maxCharacters) {
    return barcode;
  }

  const boundedCharacters = Math.max(MIN_LEFT_ELLIPSIS_LENGTH, Math.floor(maxCharacters));
  const suffixLength = clamp(
    boundedCharacters - LEFT_ELLIPSIS.length,
    MIN_BARCODE_SUFFIX_DIGITS,
    barcode.length
  );

  return `${LEFT_ELLIPSIS}${barcode.slice(-suffixLength)}`;
}

export function abbreviateStoreName(storeLabel: string): string {
  const trimmed = storeLabel.trim();
  if (!trimmed) {
    return '';
  }
  return `${trimmed[0]}..`;
}

export function formatBarcodeStoreLabel(
  detail: MasterBarcodeEntry['storeDetails'][number],
  abbreviate = false
): string {
  return abbreviate ? abbreviateStoreName(detail.storeLabel) : detail.storeLabel;
}

export function resolveBarcodeRowResponsiveLayout(
  availableWidth: number
): BarcodeRowResponsiveLayout {
  const safeWidth = Math.max(96, availableWidth);
  const textLayoutMode: BarcodeRowTextLayoutMode =
    safeWidth <= INLINE_TEXT_THRESHOLD ? 'split' : 'inline';
  const abbreviateStores = safeWidth < ABBREVIATED_STORES_THRESHOLD;
  const barcodeWidthBudget =
    textLayoutMode === 'inline'
      ? safeWidth >= 320
        ? Math.min(188, Math.max(72, safeWidth * 0.34))
        : Math.min(160, Math.max(60, safeWidth * 0.26))
      : Math.min(116, Math.max(52, safeWidth * 0.42));
  const barcodeMaxCharacters = Math.max(
    MIN_LEFT_ELLIPSIS_LENGTH,
    Math.floor(barcodeWidthBudget / MONO_CHAR_WIDTH_PX)
  );

  return {
    barcodeMaxCharacters,
    textLayoutMode,
    abbreviateStores,
  };
}

export function getBarcodeImageStripViewportWidth(imageCount: number, imageSize: number): number {
  return getBarcodeImageStripViewportWidthForAvailableSpace(
    imageCount,
    imageSize,
    Number.POSITIVE_INFINITY
  );
}

export function getBarcodeImageStripViewportWidthForAvailableSpace(
  imageCount: number,
  imageSize: number,
  maxAvailableWidth: number
): number {
  if (imageCount <= 0) {
    return 0;
  }

  const gap = 4;
  const totalWidth = imageCount * imageSize + (imageCount - 1) * gap;
  const safeAvailableWidth = Math.max(0, maxAvailableWidth);

  if (imageCount === 1) {
    return safeAvailableWidth > 0 ? Math.min(imageSize, safeAvailableWidth) : imageSize;
  }

  const minimumOverflowWidth = imageSize * 1.1 + gap;

  if (!Number.isFinite(maxAvailableWidth)) {
    return totalWidth;
  }

  if (safeAvailableWidth >= totalWidth) {
    return totalWidth;
  }

  if (safeAvailableWidth >= minimumOverflowWidth) {
    return safeAvailableWidth;
  }

  return Math.min(totalWidth, safeAvailableWidth);
}
